import * as React from "react"
import "./time-elapsed.css"
import classNames from "classnames"
import { formatHoursMinutesSecondsSinceDate } from "@nova/format"

export const formatElapsed = (durationMs: number) => {
    // calculate the hours component
    // 3.6e6 = an hour in milliseconds
    const totalSeconds = Math.floor(durationMs / 1000)
    const seconds = totalSeconds % 60
    const minutes = Math.floor((totalSeconds / 60) % 60)
    const hours = Math.floor(totalSeconds / 3600)

    const pad = (n: number) => n.toString().padStart(2, "0")

    if (durationMs < 1) return `00:00`
    if (hours > 0) {
        return `${hours}:${pad(minutes)}:${pad(seconds)}`
    } else {
        return `${minutes}:${pad(seconds)}`
    }
}

export const TimeElapsed = ({
    start,
    className,
    ...rest
}: { start: Date } & Omit<React.HTMLProps<HTMLSpanElement>, "start">) => {
    const animationRef = React.useRef<ReturnType<typeof setTimeout>>(undefined)
    const spanRef = React.useRef<HTMLSpanElement | null>(null)

    const animate = React.useCallback(() => {
        const elapsed = Date.now() - start.getTime()
        if (spanRef.current) {
            spanRef.current.innerHTML = formatElapsed(elapsed)
        }
        animationRef.current = setTimeout(animate, 1000)
    }, [start])

    React.useEffect(() => {
        animationRef.current = setTimeout(animate)
        return () => clearTimeout(animationRef.current)
    }, [start, animate])

    const elapsed = Date.now() - start.getTime()
    return (
        <span ref={spanRef} className={classNames(className, "time-elapsed mono")} {...rest}>
            {formatElapsed(elapsed)}
        </span>
    )
}

export const useTimeElapsed = (timeInMs: number | undefined) => {
    const [formatted, setFormatted] = React.useState(
        timeInMs ? formatHoursMinutesSecondsSinceDate(timeInMs) : undefined
    )

    React.useEffect(() => {
        const interval = setInterval(() => {
            if (timeInMs !== undefined) setFormatted(formatHoursMinutesSecondsSinceDate(timeInMs))
        }, 250)

        return () => {
            clearInterval(interval)
        }
    }, [timeInMs])

    return formatted
}
